import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Container
} from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TabletIcon from '@mui/icons-material/Tablet';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SiteSettings } from './SiteCustomizationManager';

interface SiteCustomizationPreviewProps {
  settings: SiteSettings;
}

type ViewportSize = 'mobile' | 'tablet' | 'desktop';

const VIEWPORT_DIMENSIONS = {
  mobile: { width: 375, height: 667 },
  tablet: { width: 768, height: 1024 },
  desktop: { width: 1440, height: 900 }
};

export const SiteCustomizationPreview: React.FC<SiteCustomizationPreviewProps> = ({ settings }) => {
  const [viewport, setViewport] = useState<ViewportSize>('desktop');

  const handleViewportChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewport: ViewportSize | null
  ) => {
    if (newViewport !== null) {
      setViewport(newViewport);
    }
  };

  const handleOpenInNewTab = () => {
    // Create a new window with the preview
    const previewWindow = window.open('', '_blank', 'width=1200,height=800');
    if (previewWindow) {
      previewWindow.document.write(generatePreviewHTML(settings));
      previewWindow.document.close();
    }
  };

  const generatePreviewHTML = (settings: SiteSettings): string => {
    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Site Preview</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: ${settings.theme.fonts.body}, sans-serif;
      font-size: ${settings.theme.fonts.bodySize}px;
      color: ${settings.theme.colors.text};
      background-color: ${settings.theme.colors.background};
      line-height: 1.6;
    }
    
    ${settings.images.background ? `
    body::before {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: url('${settings.images.background.url}');
      background-size: cover;
      background-position: center;
      opacity: ${settings.images.background.opacity};
      z-index: -1;
    }
    ` : ''}
    
    header {
      background-color: ${settings.theme.colors.primary};
      color: white;
      padding: 20px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    .header-content {
      max-width: 1200px;
      margin: 0 auto;
      display: flex;
      align-items: center;
      gap: 20px;
    }
    
    .logo {
      max-height: 60px;
      max-width: 200px;
      width: auto;
      height: auto;
    }
    
    .hero {
      position: relative;
      height: 400px;
      background-color: ${settings.theme.colors.secondary};
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    
    @media (max-width: 768px) {
      .hero {
        height: 300px;
      }
      
      .logo {
        max-height: 50px;
        max-width: 150px;
      }
      
      h1 {
        font-size: ${settings.theme.fonts.headingSize * 1.2}px !important;
      }
      
      .hero-content {
        padding: 20px !important;
      }
      
      .container {
        padding: 20px 10px !important;
      }
    }
    
    @media (max-width: 480px) {
      .hero {
        height: 250px;
      }
      
      .logo {
        max-height: 40px;
        max-width: 120px;
      }
      
      h1 {
        font-size: ${settings.theme.fonts.headingSize}px !important;
      }
    }
    
    ${settings.images.hero ? `
    .hero::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: url('${settings.images.hero.url}');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      z-index: 0;
    }
    ` : ''}
    
    .hero-content {
      position: relative;
      z-index: 1;
      text-align: center;
      color: white;
      padding: 40px;
      background-color: rgba(0, 0, 0, 0.5);
      border-radius: 8px;
    }
    
    h1, h2, h3, h4, h5, h6 {
      font-family: ${settings.theme.fonts.heading}, sans-serif;
      font-size: ${settings.theme.fonts.headingSize}px;
      margin-bottom: 16px;
    }
    
    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 40px 20px;
    }
    
    .button {
      display: inline-block;
      padding: 12px 24px;
      margin: 8px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 16px;
      text-decoration: none;
      transition: opacity 0.3s;
    }
    
    .button:hover {
      opacity: 0.9;
    }
    
    .button-primary {
      background-color: ${settings.theme.colors.primary};
      color: white;
    }
    
    .button-secondary {
      background-color: ${settings.theme.colors.secondary};
      color: white;
    }
    
    .button-accent {
      background-color: ${settings.theme.colors.accent};
      color: white;
    }
    
    footer {
      background-color: #333;
      color: white;
      padding: 40px 20px;
      margin-top: 60px;
    }
    
    .footer-content {
      max-width: 1200px;
      margin: 0 auto;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 30px;
    }
    
    .footer-section h3 {
      font-size: 18px;
      margin-bottom: 16px;
    }
    
    .footer-section a {
      color: #aaa;
      text-decoration: none;
      display: block;
      margin-bottom: 8px;
    }
    
    .footer-section a:hover {
      color: white;
    }
    
    img {
      max-width: 100%;
      height: auto;
    }
    
    @media (max-width: 768px) {
      .footer-content {
        grid-template-columns: 1fr;
        gap: 20px;
      }
      
      .button {
        padding: 10px 20px;
        font-size: 14px;
      }
    }
    
    ${settings.customCSS || ''}
  </style>
</head>
<body>
  <header>
    <div class="header-content">
      ${settings.images.logo ? `<img src="${settings.images.logo.url}" alt="${settings.images.logo.alt}" class="logo">` : ''}
      <h2>Your Event Site</h2>
    </div>
  </header>
  
  <div class="hero">
    <div class="hero-content">
      <h1>${settings.content.welcome.title}</h1>
      <p style="font-size: 20px; margin-bottom: 8px;">${settings.content.welcome.tagline}</p>
    </div>
  </div>
  
  <div class="container">
    <div style="margin-bottom: 40px;">
      ${settings.content.welcome.message}
    </div>
    
    <div style="margin-bottom: 40px;">
      <h2>Sample Buttons</h2>
      <button class="button button-primary">Primary Action</button>
      <button class="button button-secondary">Secondary Action</button>
      <button class="button button-accent">Accent Action</button>
    </div>
    
    <div>
      <h2>Sample Content</h2>
      <p>This is a preview of how your customized site will look to guests. The theme colors, fonts, and content you've configured are applied here.</p>
    </div>
  </div>
  
  <footer>
    <div class="footer-content">
      <div class="footer-section">
        <h3>Contact Information</h3>
        ${settings.content.footer.contactEmail ? `<p>Email: ${settings.content.footer.contactEmail}</p>` : ''}
        ${settings.content.footer.contactPhone ? `<p>Phone: ${settings.content.footer.contactPhone}</p>` : ''}
        ${settings.content.footer.address ? `<p>Address: ${settings.content.footer.address}</p>` : ''}
      </div>
      
      ${(settings.content.footer.socialLinks.facebook || 
         settings.content.footer.socialLinks.instagram || 
         settings.content.footer.socialLinks.twitter ||
         (settings.content.footer.socialLinks.custom && settings.content.footer.socialLinks.custom.length > 0)) ? `
      <div class="footer-section">
        <h3>Follow Us</h3>
        ${settings.content.footer.socialLinks.facebook ? `<a href="${settings.content.footer.socialLinks.facebook}" target="_blank">Facebook</a>` : ''}
        ${settings.content.footer.socialLinks.instagram ? `<a href="${settings.content.footer.socialLinks.instagram}" target="_blank">Instagram</a>` : ''}
        ${settings.content.footer.socialLinks.twitter ? `<a href="${settings.content.footer.socialLinks.twitter}" target="_blank">Twitter</a>` : ''}
        ${settings.content.footer.socialLinks.custom?.map(link => 
          link.name && link.url ? `<a href="${link.url}" target="_blank">${link.icon || ''} ${link.name}</a>` : ''
        ).join('') || ''}
      </div>
      ` : ''}
    </div>
  </footer>
</body>
</html>
    `;
  };

  const dimensions = VIEWPORT_DIMENSIONS[viewport];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <ToggleButtonGroup
          value={viewport}
          exclusive
          onChange={handleViewportChange}
          aria-label="viewport size"
        >
          <ToggleButton value="mobile" aria-label="mobile view">
            <PhoneAndroidIcon sx={{ mr: 1 }} />
            Mobile
          </ToggleButton>
          <ToggleButton value="tablet" aria-label="tablet view">
            <TabletIcon sx={{ mr: 1 }} />
            Tablet
          </ToggleButton>
          <ToggleButton value="desktop" aria-label="desktop view">
            <DesktopWindowsIcon sx={{ mr: 1 }} />
            Desktop
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={handleOpenInNewTab}
        >
          Open in New Tab
        </Button>
      </Box>

      <Paper
        sx={{
          width: '100%',
          height: 600,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          backgroundColor: '#f0f0f0',
          overflow: 'auto',
          p: 2
        }}
      >
        <Box
          sx={{
            width: dimensions.width,
            minHeight: dimensions.height,
            backgroundColor: 'white',
            boxShadow: 3,
            overflow: 'auto',
            transition: 'all 0.3s ease'
          }}
        >
          <iframe
            srcDoc={generatePreviewHTML(settings)}
            style={{
              width: '100%',
              height: dimensions.height,
              border: 'none'
            }}
            title="Site Preview"
          />
        </Box>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Preview dimensions: {dimensions.width}x{dimensions.height}px
      </Typography>
    </Box>
  );
};
